import type { QueryClient } from "@tanstack/react-query";
import type { AuthContextValue, AuthUser } from "../contexts/auth";
import { BrokeredProvider } from "../contexts/auth/types/auth";
import type { EventBus } from "../contexts/events";
import { AgendaManager } from "../managers/agenda";
import { ApiManager, TARGET_API_V2 } from "../managers/api";
import type { ClientInfo } from "../managers/api/types/clientInfo";
import { BannerManager } from "../managers/banner";
import { CO2Manager } from "../managers/co2";
import { ConsentManager } from "../managers/consentManager";
import { ContactManager } from "../managers/contact";
import { ConversationManager } from "../managers/conversation";
import type { ConversationContract } from "../managers/conversation/types/conversation";
import { CustomViewManager } from "../managers/customView";
import { CustomViewMembership } from "../managers/customView/CustomViewMembership";
import { CustomViewState } from "../managers/customView/CustomViewState";
import { SmartLabelStore } from "../managers/customView/SmartLabelStore";
import { DataManager } from "../managers/dataManager/DataManager";
import { GoogleManager } from "../managers/dataManager/google";
import { GraphManager } from "../managers/dataManager/graph";
import { IDataApi } from "../managers/dataManager/IDataApi";
import { IaManager } from "../managers/ia";
import { LicenseManager } from "../managers/license";
import { LicenseState } from "../managers/license/LicenseState";
import { MailManager } from "../managers/mail";
import { MailActions } from "../managers/mail/MailActions";
import { MailAnalysisState } from "../managers/mail/MailAnalysisState";
import { MailReader } from "../managers/mail/MailReader";
import { MailRecord } from "../managers/mail/MailRecord";
import { MailSync } from "../managers/mail/MailSync";
import { MailViews } from "../managers/mail/MailViews";
import { MusicManager } from "../managers/music";
import { SttManager } from "../managers/stt";
import { UserManager } from "../managers/user";
import { FeatureFlags } from "../managers/user/FeatureFlags";
import { MailAnalysisFlag } from "../managers/user/MailAnalysisFlag";
import { toFocusSortMode } from "../managers/user/types/settings";
import { UserContextLoader } from "../managers/user/UserContextLoader";
import { UserProfileLoader } from "../managers/user/UserProfileLoader";
import { UserTodos } from "../managers/user/UserTodos";
import { logger, LOGS } from "../utils";
import type { UserTimezoneReader } from "../utils/date/userTimezone";
import { createQueryClient } from "./createQueryClient";
import { SessionEventBus } from "./SessionEventBus";

export type SdkConfig = {
    eventBus: EventBus;
    authContext: AuthContextValue;
    apiTarget: TARGET_API_V2;
    user: AuthUser;
    userMail: string;
    /** Identité du client, pour `X-Mv-Client` (F-1.24). Absente : en-têtes non envoyés. */
    clientInfo?: ClientInfo;
    /** Lecture du fuseau de l'appareil, pour `userTimezone` (F-1.19). Absente : `Intl`. */
    readUserTimezone?: UserTimezoneReader;
    /** Contrat de l'assistant avec l'Api-Ai. Absent : v1. */
    conversationContract?: ConversationContract;
};

export type Sdk = {
    dataManager: DataManager;
    apiManager: ApiManager;
    consentManager: ConsentManager;
    co2Manager: CO2Manager;
    iaManager: IaManager;
    licenseManager: LicenseManager;
    bannerManager: BannerManager;
    userManager: UserManager;
    customViewManager: CustomViewManager;
    mailManager: MailManager;
    musicManager: MusicManager;
    agendaManager: AgendaManager;
    conversationManager: ConversationManager;
    sttManager: SttManager;
    contactManager: ContactManager;
    sessionEventBus: SessionEventBus;
    queryClient: QueryClient;
    /** Termine la session : plus aucun événement, cache des requêtes vidé. Idempotent. */
    dispose: () => void;
};

export function createSdk({
    eventBus: appEventBus,
    authContext,
    apiTarget,
    user,
    userMail,
    clientInfo,
    readUserTimezone,
    conversationContract,
}: SdkConfig): Sdk {
    const eventBus = new SessionEventBus(appEventBus);
    const queryClient = createQueryClient();
    let dataApi: IDataApi | undefined;
    // For Keycloak, detect the brokered provider to determine which data API to use
    const brokeredProvider = authContext.getBrokeredProvider();
    logger.info(
        LOGS.ServiceProvider,
        `  Keycloak brokered provider detected: ${brokeredProvider}`,
    );

    if (brokeredProvider === BrokeredProvider.Microsoft) {
        dataApi = new GraphManager(eventBus, authContext);
    } else if (brokeredProvider === BrokeredProvider.Google) {
        dataApi = new GoogleManager(eventBus, authContext);
    } else {
        logger.error(
            LOGS.ServiceProvider,
            `  Unknown brokered provider: ${brokeredProvider}, defaulting to GraphManager`,
        );
        dataApi = new GraphManager(eventBus, authContext);
    }

    if (!dataApi) {
        logger.error(LOGS.ServiceProvider, `DataManager is not instanciate`);
        throw new Error("DataManager is not instanciate");
    }

    const dataManager = new DataManager(eventBus, dataApi);
    const apiManager = new ApiManager(
        eventBus,
        authContext,
        apiTarget,
        clientInfo,
        readUserTimezone,
    );
    const licenseState = new LicenseState(eventBus, queryClient);
    const featureFlags = new FeatureFlags();
    const userTodos = new UserTodos(apiManager, queryClient);
    const contextLoader = new UserContextLoader(
        eventBus,
        apiManager,
        licenseState,
    );
    const profileLoader = new UserProfileLoader(
        user,
        eventBus,
        authContext,
        apiManager,
        licenseState,
        queryClient,
    );
    const iaManager = new IaManager(
        eventBus,
        apiManager,
        licenseState,
        profileLoader,
    );
    const licenseManager = new LicenseManager(
        eventBus,
        authContext,
        licenseState,
        profileLoader,
        apiManager,
    );
    const mailAnalysisFlag = new MailAnalysisFlag(licenseManager, featureFlags);
    const bannerManager = new BannerManager(eventBus, apiManager, queryClient);
    const consentManager = new ConsentManager(eventBus, profileLoader);
    const co2Manager = new CO2Manager(eventBus);
    const mailRecord = new MailRecord();
    const contactManager = new ContactManager(
        eventBus,
        apiManager,
        dataManager,
        mailRecord,
    );
    const smartLabelStore = new SmartLabelStore(eventBus);
    const analysisState = new MailAnalysisState();
    const mailSync = new MailSync(
        eventBus,
        mailRecord,
        analysisState,
        smartLabelStore,
        apiManager,
        dataManager,
        licenseManager,
        featureFlags,
        profileLoader,
        mailAnalysisFlag,
    );
    const customViewState = new CustomViewState();
    const customViewManager = new CustomViewManager(
        eventBus,
        userMail,
        smartLabelStore,
        customViewState,
        new CustomViewMembership(
            eventBus,
            apiManager,
            mailSync,
            customViewState,
            smartLabelStore,
            () =>
                toFocusSortMode(
                    profileLoader.cachedProfile?.settings?.displayPreferences
                        ?.focusSortMode,
                ),
        ),
        iaManager,
        dataManager,
        mailSync,
        featureFlags,
        contextLoader,
        apiManager,
        contactManager,
    );
    const mailReader = new MailReader(
        eventBus,
        mailRecord,
        mailSync,
        apiManager,
        dataManager,
        customViewManager,
        contactManager,
    );
    const mailActions = new MailActions(
        eventBus,
        mailRecord,
        mailSync,
        apiManager,
        dataManager,
        userTodos,
        customViewManager,
        co2Manager,
    );
    const mailViews = new MailViews(
        eventBus,
        mailRecord,
        mailSync,
        mailReader,
        dataManager,
        customViewManager,
        queryClient,
    );
    const mailManager = new MailManager(
        eventBus,
        mailRecord,
        analysisState,
        mailSync,
        mailReader,
        mailActions,
        mailViews,
        apiManager,
        dataManager,
        licenseManager,
        profileLoader,
        mailAnalysisFlag,
        iaManager,
        customViewManager,
        co2Manager,
        contactManager,
        queryClient,
    );
    const userManager = new UserManager(
        user,
        eventBus,
        authContext,
        apiTarget,
        profileLoader,
        featureFlags,
        userTodos,
        contextLoader,
        mailAnalysisFlag,
        dataManager,
        iaManager,
        licenseManager,
        bannerManager,
        mailManager,
        consentManager,
        customViewManager,
        apiManager,
    );
    const musicManager = new MusicManager(eventBus);
    const agendaManager = new AgendaManager(eventBus, dataManager, mailManager);
    const conversationManager = new ConversationManager(
        eventBus,
        iaManager,
        mailManager,
        dataManager,
        userManager,
        agendaManager,
        contactManager,
        conversationContract,
    );
    const sttManager = new SttManager(apiManager);

    logger.info(LOGS.ServiceProvider, " => all managers instanciated");

    userManager._registerEvents();
    mailManager._registerEvents();
    contactManager._registerEvents();
    bannerManager._registerEvents();
    iaManager._registerEvents();
    customViewManager._registerEvents();
    agendaManager._registerEvents();

    logger.info(
        LOGS.ServiceProvider,
        " => all managers sets and events registrered",
    );

    return {
        sessionEventBus: eventBus,
        queryClient,
        dataManager,
        apiManager,
        consentManager,
        co2Manager,
        iaManager,
        licenseManager,
        bannerManager,
        userManager,
        customViewManager,
        mailManager,
        musicManager,
        agendaManager,
        conversationManager,
        sttManager,
        contactManager,
        dispose: () => {
            eventBus.dispose();
            queryClient.clear();
            queryClient.unmount();
        },
    };
}
